import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { Drawer } from 'vaul';
import { Listing, OpeningHoursResult } from '../../types';
import { getArea } from '../../config/areas';
import { LatLng, mapPointOf, navTargetOf } from '../../utils/geo';
import { displayAddress, pickLang } from '../../utils/listingDisplay';
import { useIsMobile } from '../../hooks/useIsMobile';
import LazyDirectoryMap from './map/LazyDirectoryMap';
import type { MapItem } from './map/types';
import CategoryIcon from './CategoryIcon';
import DistanceText from './DistanceText';
import MapListingPopup from './MapListingPopup';
import NavigateButton from './NavigateButton';
import { listingHref } from './ListingCard';
import { OpenStatusBadge } from './StatusBadge';

const MAP_SHADOW = 'shadow-[0_2px_6px_rgba(0,0,0,.04),0_4px_8px_rgba(0,0,0,.10)]';
const SNAP_POINTS = [0.18, 0.5, 0.92] as const;
const NAV_HEIGHT = 64;

interface DirectoryMapViewProps {
  results: Listing[];
  hoursById: Record<string, OpeningHoursResult>;
  distanceOf: (listing: Listing) => number | undefined;
  userLocation: LatLng | null;
  /** Search box and filter chips; shown above the list (in the slide-up panel on phones). */
  filterBar: ReactNode;
  /** Status lines (Near me message, result count). */
  notices?: ReactNode;
  title: string;
  /** Listing to open on the map (?item=, from a card's map button). */
  focusId?: string | null;
}

/** Map + list side by side; on phones a full-screen map with a slide-up list (plan 7, 8.3). */
export default function DirectoryMapView({
  results,
  hoursById,
  distanceOf,
  userLocation,
  filterBar,
  notices,
  title,
  focusId = null,
}: DirectoryMapViewProps) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const isMobile = useIsMobile();
  const [selectedId, setSelectedId] = useState<string | null>(focusId);
  const [snap, setSnap] = useState<number | string | null>(SNAP_POINTS[1]);
  const rowRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const items = useMemo<MapItem[]>(
    () =>
      results.flatMap((l) => {
        const point = mapPointOf(l.location);
        return point
          ? [{ id: l.id, ...point, category: l.category, title: pickLang(l.nameEn, l.nameHe, isHebrew) }]
          : [];
      }),
    [results, isHebrew]
  );
  const notOnMap = results.length - items.length;
  const byId = useMemo(() => new Map(results.map((l) => [l.id, l])), [results]);

  useEffect(() => {
    if (focusId) setSelectedId(focusId);
  }, [focusId]);

  // Selection disappears when a filter hides it (not while the listings are still arriving).
  useEffect(() => {
    if (selectedId && byId.size > 0 && !byId.has(selectedId)) setSelectedId(null);
  }, [byId, selectedId]);

  useEffect(() => {
    if (selectedId) rowRefs.current[selectedId]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedId]);

  // Full-screen map on phones: stop the page behind it from scrolling.
  useEffect(() => {
    if (!isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobile]);

  const selectFromList = (id: string) => {
    setSelectedId((prev) => (prev === id ? null : id));
    // On a phone, lower the panel so the pin is visible.
    if (isMobile && snap === SNAP_POINTS[2]) setSnap(SNAP_POINTS[1]);
  };

  const bottomInset = isMobile && typeof snap === 'number' ? Math.round(snap * (window.innerHeight - NAV_HEIGHT)) : 0;

  const map = (
    <LazyDirectoryMap
      items={items}
      selectedId={selectedId}
      onSelect={setSelectedId}
      userLocation={userLocation}
      bottomInset={bottomInset}
      topInset={isMobile ? 60 : 0}
      zoomButtons={!isMobile}
      renderPopup={(id) => {
        const listing = byId.get(id);
        return listing ? <MapListingPopup listing={listing} hours={hoursById[id]} /> : null;
      }}
    />
  );

  const rows = (
    <>
      {notices}
      {notOnMap > 0 && <p className="text-xs text-ash">{t('directory.map.notOnMap', { count: notOnMap })}</p>}
      {results.length === 0 && <p className="py-8 text-center text-sm text-ash">{t('directory.filter.noResults')}</p>}
      <ul className="space-y-2" aria-label={t('directory.map.resultsList')}>
        {results.map((listing) => {
          const selected = listing.id === selectedId;
          const name = pickLang(listing.nameEn, listing.nameHe, isHebrew);
          const address = displayAddress(listing, isHebrew);
          const distance = distanceOf(listing);
          const onMap = !!mapPointOf(listing.location);
          return (
            <li key={listing.id}>
              <div
                className={`rounded-[16px] border transition-colors ${
                  selected ? 'border-weekday-600 bg-weekday-50' : 'border-ink/10 bg-paper hover:border-ink/30'
                }`}
              >
                <button
                  type="button"
                  ref={(el) => {
                    rowRefs.current[listing.id] = el;
                  }}
                  onClick={() => (onMap ? selectFromList(listing.id) : undefined)}
                  aria-pressed={onMap ? selected : undefined}
                  className="w-full text-start p-3 flex gap-2.5 items-start min-h-11"
                >
                  {listing.photoUrl ? (
                    <img src={listing.photoUrl} alt="" className="w-10 h-10 shrink-0 rounded-[8px] object-cover" />
                  ) : (
                    <CategoryIcon category={listing.category} className="w-4 h-4 mt-0.5 shrink-0 text-ink/70" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold text-ink text-sm truncate">{name}</span>
                    {(address || distance !== undefined) && (
                      <span className="flex items-center gap-1 text-xs text-ash mt-0.5 truncate">
                        <MapPin className="w-3 h-3 shrink-0" aria-hidden />
                        {distance !== undefined && <DistanceText meters={distance} className="font-medium text-ink" />}
                        {distance !== undefined && address && ' · '}
                        <span className="truncate">{address}</span>
                      </span>
                    )}
                    {listing.category !== 'shul' && (
                      <span className="flex mt-1">
                        <OpenStatusBadge listing={listing} result={hoursById[listing.id]} />
                      </span>
                    )}
                  </span>
                </button>
                {selected && (
                  <div className="flex flex-wrap items-center gap-2 px-3 pb-3">
                    <Link
                      to={listingHref(listing)}
                      className="text-xs font-medium border border-ink/15 text-ink rounded-full px-3 py-1.5 hover:bg-fog"
                    >
                      {t('directory.action.details')}
                    </Link>
                    <NavigateButton
                      target={navTargetOf(listing.location, getArea(listing.location?.areaId ?? ''), isHebrew)}
                      variant="compact"
                    />
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );

  if (isMobile) {
    return (
      <div className="fixed inset-x-0 top-[var(--nav-bottom)] bottom-0 z-20">
        <div className="absolute inset-0">{map}</div>
        <Drawer.Root
          open
          modal={false}
          dismissible={false}
          snapPoints={[...SNAP_POINTS]}
          activeSnapPoint={snap}
          setActiveSnapPoint={setSnap}
        >
          <Drawer.Portal>
            <Drawer.Content
              className={`fixed inset-x-0 bottom-0 z-30 flex h-full flex-col rounded-t-[16px] bg-paper outline-none ${MAP_SHADOW}`}
              dir={isHebrew ? 'rtl' : 'ltr'}
            >
              <Drawer.Handle
                preventCycle
                className="mx-auto mt-2 mb-1 !h-6 !w-full !max-w-none !bg-transparent opacity-100 rounded-none"
              >
                <span className="mx-auto block w-10 h-1 rounded-full bg-ink/20" />
              </Drawer.Handle>
              <Drawer.Title className="sr-only">{title}</Drawer.Title>
              <div className="flex-1 overflow-y-auto px-4 pb-6 space-y-3 overscroll-contain" data-vaul-no-drag>
                {filterBar}
                {rows}
              </div>
            </Drawer.Content>
          </Drawer.Portal>
        </Drawer.Root>
      </div>
    );
  }

  return (
    <div className="flex gap-4">
      <div className="w-2/5 order-2 space-y-3 max-h-[75vh] overflow-y-auto pe-1">
        {filterBar}
        {rows}
      </div>
      <div className="relative w-3/5 order-1 h-[75vh] rounded-[16px] overflow-hidden border border-ink/10">{map}</div>
    </div>
  );
}
